import { useState, useMemo, useRef, useEffect } from 'react';

import Papa from 'papaparse';
import * as XLSX from 'xlsx';



export function PaginatedTable({ rows, PAGE_SIZE = 10 }) {
    const [page, setPage] = useState(1);

    // Memo‑compute the slice that should be shown on the current page
    const { pageRows, totalPages } = useMemo(() => {
        const totalPages = Math.ceil(rows.length / PAGE_SIZE) || 1;
        const start = (page - 1) * PAGE_SIZE;
        const end = start + PAGE_SIZE;
        return { pageRows: rows.slice(start, end), totalPages };
    }, [rows, page]);

    const goTo = (newPage) =>
        setPage((p) => Math.min(Math.max(newPage, 1), totalPages));

    if (!rows?.length) return <p>No records to display.</p>;

    /* ---------------------------------------------
       The table assumes the rows are plain objects.
       It grabs the keys from the first row to build
       headers automatically. You can customise as
       needed (rename headers, add styling, etc.).
    --------------------------------------------- */
    const headers = Object.keys(rows[0]);

    return (
        <div className="max-w-full overflow-x-auto space-y-4">
            <table className="table table-bordered table-hover align-middle">
                <thead className="table-light">
                    <tr>
                        {headers.map((h) => (
                            <th key={h} className="p-2 border-b text-left capitalize">
                                {h}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {pageRows.map((row, idx) => (
                        <tr key={idx} className="odd:bg-white even:bg-gray-50">
                            {headers.map((h) => (
                                <td key={h} className="p-2 border-b whitespace-nowrap">
                                    {String(row[h] ?? "")}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Pagination controls */}
            <div className="flex items-center justify-center gap-2 mt-2">
                <button
                    onClick={() => goTo(page - 1)}
                    disabled={page === 1}
                    className="px-2 py-1 border rounded disabled:opacity-40"
                >
                    ‹ Prev
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                    <button
                        key={n}
                        onClick={() => goTo(n)}
                        className={`w-8 h-8 border rounded mx-1 ${n === page ? "bg-primary text-white" : ""
                            }`}
                    >
                        {n}
                    </button>
                ))}
                <button
                    onClick={() => goTo(page + 1)}
                    disabled={page === totalPages}
                    className="px-2 py-1 border rounded disabled:opacity-40"
                >
                    Next ›
                </button>
            </div>
        </div>
    );
}
export default ({
    onSelect,
    label = "Upload CSV or Excel:",
    PAGE_SIZE = 10
}) => {
    const [data, setData] = useState([]);   // holds parsed array
    const [fileName, setFileName] = useState('');
    const fileRef = useRef(null);

    useEffect(() => {
        onSelect && onSelect(data);
    }, [data])
    // utils/cleanRows.js (or inline in the component)
    const cleanRows = rows =>
        rows.filter(row =>
            // keep the row only if SOME cell isn't empty / null / '' / []
            Object.values(row).some(v =>
                Array.isArray(v) ? v.length > 0 : String(v ?? '').trim() !== ''
            )
        );

    const handleFile = e => {
        const file = e.target.files[0];
        if (!file) return;
        setFileName(file.name);

        const ext = file.name.split('.').pop().toLowerCase();

        if (ext === 'csv') {
            readCSV(file);
        } else if (['xlsx', 'xls', 'xlsm'].includes(ext)) {
            readExcel(file);
        } else {
            alert('Unsupported file type');
        }
    };

    const handleClear = () => {
        if (fileRef.current) fileRef.current.value = '';
        setFileName('');
        setData([]);
    };
    // ---- CSV ----
    const readCSV = file => {
        Papa.parse(file, {
            header: true,        // ⇠ false if you want raw rows
            skipEmptyLines: true,
            complete: results => setData(cleanRows(results.data)),
            error: err => console.error(err)
        });
    };

    // ---- Excel (.xlsx, .xls, .xlsm) ----
    const readExcel = file => {
        const reader = new FileReader();
        reader.onload = evt => {
            const wb = XLSX.read(evt.target.result, { type: 'binary' });
            const firstSheet = wb.SheetNames[0];
            const ws = wb.Sheets[firstSheet];

            /* `xlsx` output options */
            const rows = XLSX.utils.sheet_to_json(ws, {
                header: 0,         // 0 = use first row as headers (objects)
                defval: '',        // default value for empty cells
                blankrows: false
            });
            setData(cleanRows(rows));       // array of objects
        };
        reader.readAsBinaryString(file);
    };
    const downloadSample = () => {
        const sample = "Name,Mobile,Country Code\nJohn Doe,7911112222,+91\nJane Smith,7922223333,+44";
        const blob = new Blob([sample], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "invitees_sample.csv";
        a.click();
        URL.revokeObjectURL(url);
    }

    return (
        <div>
            <div className="mb-3">

                <div class="row">
                    <label className="form-label fw-semibold">
                        {label}
                    </label>
                    
                    <div class="file-upload"><div class="row ">
                        <div class="col-auto mt-2">
                       <div className='d-flex'>
                         <input type="file" id="xlsxFile_new" class="btn-upload"
                            accept=".csv,.xlsx,.xls,.xlsm"
                            onChange={handleFile}
                            ref={fileRef}
                        />
                         {fileName && (
                            <button
                                type="button"
                                className="btn btn-success ms-2"
                                onClick={handleClear}
                            >
                                Clear 
                            </button>

                        )}
                       </div>
                       
                        <span>Allowed file type :- .csv,.xlsx,.xls</span><br />
                        <span>File size :- 30 MB</span></div>
                        <div class="col-md-7 col-xl-6">
                        </div><div class="col-md-12">
                            <a onClick={downloadSample} href='javascrip:void()'>
                                <span>Download Sample</span></a></div>
                    </div>
                        </div></div>

             

            </div>

            {fileName && (
                <p className="text-muted mb-3">Selected File: {fileName}</p>
            )}
            {data.length > 0 && <PaginatedTable rows={data} PAGE_SIZE={PAGE_SIZE} />}
        </div>
    );
}